/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * ONE PERSON, WHICH SOURCE OF WEALTH LINK (v1_897).
 *
 * A person can hold more than one source of wealth link on a case: a resend
 * after the interview was finished created a new link, because the server
 * reused only a link still in progress. The panel then took the first link in
 * a list ordered newest first, so on Diment (66f89346) the director's finished
 * interview of 26/09 (151952c2) was hidden behind an unused link of 28/09
 * (52672ac9): the case showed the interview not done and an empty transcript.
 *
 * The rule, everywhere a person's link is read:
 *   - a person's links are matched by name (any form the case keeps for the
 *     person, v1_888) or, for a link that carries no name, by the address it
 *     was sent to;
 *   - the most recent COMPLETED interview is the person's interview;
 *   - with none completed, the newest open link;
 *   - a newer open link after a completed one is reported, never silent;
 *   - except when the officer asked for a NEW interview: then the open link is
 *     the person's, the completed one is the previous interview, and the person
 *     is not complete until the new interview is.
 * Pure. The person matcher is passed in (the dashboard's, or the shared one).
 */
import { samePerson as sharedSamePerson, normEmail } from './person-name-match.js';

export const isSowLink = (s) => !!s && (s.status === 'sow_interview' || s.status === 'sow_complete');
export const linkParentId = (s) => (s && (s.parent_session_id || (s.collected_data && s.collected_data.parentSessionId))) || null;
// The link's own column only: collected_data.contactName on a source of wealth
// link can hold the person's first chat message, not their name (Diment
// 151952c2), because the interview stored its first answer there.
const linkName = (s) => String((s && s.contact_name) || '').trim();

export function personNames(person) {
  const p = person || {};
  const out = [p.name];
  for (const a of (Array.isArray(p.alsoListedAs) ? p.alsoListedAs : [])) out.push(typeof a === 'string' ? a : (a && a.name));
  return out.map(n => String(n || '').trim()).filter(Boolean);
}

export function linksForPerson(children, parentId, person, match = sharedSamePerson) {
  const names = personNames(person);
  const emails = [person && person.email, person && person.sowInterviewEmail].map(normEmail).filter(Boolean);
  return (Array.isArray(children) ? children : []).filter(s => {
    if (!isSowLink(s)) return false;
    if (parentId != null && String(linkParentId(s)) !== String(parentId)) return false;
    const nm = linkName(s);
    if (nm) return names.some(n => match(nm, n));
    return emails.length > 0 && emails.includes(normEmail(s.contact_email));
  });
}

const created = (s) => new Date((s && s.created_at) || 0).getTime() || 0;
const touched = (s) => new Date((s && (s.updated_at || s.created_at)) || 0).getTime() || 0;

const at = (v) => new Date(v || 0).getTime() || 0;

// v1_897: the officer asked this person for a NEW interview and it is not done
// yet. Read from the person's row on the case, which the server writes when the
// request is made (the link's own data is rewritten by the interview on every
// turn). Done once a completion is stamped after the request.
export function sowNewInterviewPending(person) {
  const req = at(person && person.sowNewInterviewRequestedAt);
  if (!req) return false;
  const doneAt = Math.max(at(person.sowCompletedAt), at(person.sowInterviewDate));
  return !(doneAt && doneAt >= req);
}

export function sowLinkForPerson(children, parentId, person, match = sharedSamePerson) {
  const mine = linksForPerson(children, parentId, person, match);
  const completed = mine.filter(s => s.status === 'sow_complete').sort((a, b) => touched(b) - touched(a))[0] || null;
  const open = mine.filter(s => s.status === 'sow_interview').sort((a, b) => created(b) - created(a))[0] || null;
  // v1_897: a new interview the officer asked for on purpose. Signalled by the
  // person's row (durable) or, until the interview rewrites it, by the newest
  // open link carrying newInterviewAfter. While it is outstanding the open link
  // is the person's link, the completed one is the PREVIOUS interview, and the
  // person does not count as complete.
  const p = person || {};
  const od = (open && open.collected_data) || {};
  const previousId = (p.sowPreviousInterview && p.sowPreviousInterview.link) || od.newInterviewAfter || null;
  const requestedAt = p.sowNewInterviewRequestedAt || od.newInterviewRequestedAt || (od.newInterviewAfter ? open.created_at : null) || null;
  let newInterviewRequested = false;
  let previous = null;
  if (requestedAt || previousId) {
    previous = (previousId && mine.find(s => String(s.id) === String(previousId))) || null;
    const tReq = at(requestedAt);
    const newerDone = mine.some(s => s.status === 'sow_complete' && String(s.id) !== String(previousId)
      && (tReq ? touched(s) >= tReq : (previous ? created(s) > created(previous) : true)));
    const rowDone = !!p.sowNewInterviewRequestedAt && !sowNewInterviewPending(p);
    newInterviewRequested = !newerDone && !rowDone;
    if (newInterviewRequested && !previous) previous = completed;
    if (!newInterviewRequested) previous = null;
  }
  return {
    link: newInterviewRequested ? open : (completed || open || null),
    completed,
    open,
    all: mine,
    isComplete: !!completed && !newInterviewRequested,
    newInterviewRequested,
    requestedAt: newInterviewRequested ? requestedAt : null,
    previous,
    previousCompletedAt: previous ? (previous.updated_at || previous.created_at) : (newInterviewRequested && p.sowPreviousInterview ? p.sowPreviousInterview.completedAt || null : null),
    laterOpenAfterComplete: !newInterviewRequested && !!(completed && open && created(open) > created(completed)),
  };
}

const ukDay = (iso) => { const d = new Date(iso || ''); return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10).split('-').reverse().join('/'); };

// The line the panel shows when a newer unused link sits after a finished
// interview. No dashes.
export function laterLinkNote(pick) {
  if (!pick || !pick.laterOpenAfterComplete) return '';
  return `A newer link was sent on ${ukDay(pick.open.created_at)} and has not been completed; the interview completed on ${ukDay(pick.completed.updated_at || pick.completed.created_at)} is the one shown.`;
}

// v1_897: the person's status while a new interview is outstanding, and the
// line that names the previous interview. No dashes.
export function newInterviewStatus(pick) {
  if (!pick || !pick.newInterviewRequested) return '';
  const day = ukDay(pick.requestedAt);
  return `New interview requested${day ? ` on ${day}` : ''}, awaiting the person`;
}
export function previousInterviewNote(pick) {
  if (!pick || !pick.newInterviewRequested) return '';
  const day = ukDay(pick.previousCompletedAt);
  return `Previous interview${day ? ` completed on ${day}` : ''}. It does not count as complete while the new interview is outstanding.`;
}
export function sowDay(iso) { return ukDay(iso); }
